import { useMemo, useState } from 'react';
import { CTAButton } from '~/components/common/cta-button';
import { IconButton } from '~/components/ui/IconButton';
import { Card } from '~/components/ui/card';
import { Textarea } from '~/components/ui/textarea';
import { useCopyToClipboard } from '~/hooks/use-copy-to-clipboard';
import { cn } from '~/lib/utils';
import { ChatMessageSchema } from '~/services/inference/chat';
import { CheckIcon, CopyIcon } from '@radix-ui/react-icons';
import Markdown from '../markdown';
import ChatAvatar from './chat-avatar';

// eslint-disable-next-line complexity
export default function ChatMessage({
  message,
  returnToolCallResponse,
  nextMessage,
  markdown,
}: {
  message: ChatMessageSchema;
  returnToolCallResponse: (response: string) => void;
  nextMessage?: ChatMessageSchema;
  markdown?: boolean;
}) {
  const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 });
  const [toolCallResponse, setToolCallResponse] = useState<string>('');
  const parsedJsonContent = useMemo(() => {
    if (!message.tool_calls && typeof message.content === 'string') return null;
    try {
      return JSON.parse(message.content as string);
    } catch {
      return null;
    }
  }, [message.content]);

  return (
    <div
      className={cn(
        'flex items-start gap-4 p-5',
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
        message.metadata?.used === false ? 'opacity-40' : undefined,
      )}
    >
      <ChatAvatar role={message.role} />
      <div className="group flex flex-1 justify-between gap-2">
        <div className="flex-1">
          {message.role === 'assistant' && message.content.length < 2 && (
            <div className="mt-1 flex items-start justify-start">
              <div className="flex h-6 space-x-1 ">
                <span className="typing-dots animate-loader"></span>
                <span className="typing-dots animate-loader animation-delay-200"></span>
                <span className="typing-dots animate-loader animation-delay-400"></span>
              </div>
            </div>
          )}
          {message.role === 'function' && (
            <Card>
              <div className="m-3 flex gap-x-1">
                <p className="text-gray-500 ">
                  /** Your response that was sent to the model as the returned value from the function call it made */
                </p>
              </div>
              <div className="m-3 flex flex-row items-center gap-x-1">
                <p className="font-bold">{message?.name}( ... )</p>
              </div>

              <div className="ml-8 mr-3 flex flex-row gap-x-1">
                <p className="font-bold">return "</p>
                <p className="text-gray-500">{message.content as string}</p>
                <p className="font-bold">"</p>
              </div>
              <div className="m-3 flex flex-row items-center gap-x-1">
                <p className="font-bold"> {'}'}</p>
              </div>
            </Card>
          )}
          {typeof message.content === 'string' && !message.tool_calls && markdown && (
            <Markdown
              key={message.id}
              content={message.role === 'user' ? message.content.replace(/\n/giu, '\n\n') : message.content}
            />
          )}
          {typeof message.content === 'string' && !message.tool_calls && !markdown && (
            <p key={message.id} className="whitespace-pre-line">
              {message.role === 'user' ? message.content.replace(/\n/giu, '\n\n') : message.content}
            </p>
          )}
          {typeof message.content === 'string' && message.tool_calls && (
            <Card>
              <div className="m-3 flex flex-row items-center gap-x-1">
                <p className="font-bold">{parsedJsonContent.name}(</p>
                <p className="text-gray-500">{parsedJsonContent.arguments}</p>
                <p className="font-bold">) {!nextMessage && '{'}</p>
              </div>
              {!nextMessage && (
                <>
                  <div className="m-3 ml-8 flex gap-x-1">
                    <p className="text-gray-500 ">
                      /** The value returned will be sent back to the model as a string. The model will then respond to
                      your original query. This is what you would do programmatically on the backend using our API to
                      handle tool calls in your application. */
                    </p>
                  </div>
                  <div className="ml-8 mr-3 flex gap-x-1">
                    <p className="mr-2 font-bold">return</p>
                    <Textarea
                      className="w-full"
                      placeholder="your response here..."
                      value={toolCallResponse}
                      onChange={(e) => {
                        setToolCallResponse(e.currentTarget.value);
                      }}
                      rows={4}
                    />
                  </div>
                  <div className="m-3 flex flex-row items-center gap-x-1">
                    <p className="font-bold"> {'}'}</p>
                  </div>
                </>
              )}
              {!nextMessage && (
                <CTAButton onClick={() => returnToolCallResponse(toolCallResponse)} className="m-3">
                  Return function call
                </CTAButton>
              )}
            </Card>
          )}
          {Array.isArray(message.content) &&
            message.content.map((content, index) => {
              if (content.type === 'text') {
                const text =
                  message.role === 'user'
                    ? // input
                      (content.text || '').replace(/\n/giu, '\n\n')
                    : content.text || '';

                return markdown ? (
                  <Markdown key={index} content={text} />
                ) : (
                  <p key={index} className="whitespace-pre-line">
                    {text}
                  </p>
                );
              }

              return (
                <Card
                  key={index}
                  className="p2 w-30 h-30 md:max-h-lg mt-2 flex max-h-[100vw-20vw] max-w-[100vw-20vw] overflow-hidden max-sm:justify-center md:max-w-lg"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={content.image_url?.url}
                    className="md:max-h-lg max-h-[100vw-20vw] max-w-[100vw-20vw] md:max-w-lg"
                  />
                </Card>
              );
            })}

          {Boolean(message?.metadata?.averageTokenTime) && (
            <div className="mt-1 flex items-center justify-start md:justify-end">
              <span className="text-xs text-zinc-400">
                {message.metadata?.firstTokenTime?.toFixed(0) || '-- '}ms initial latency{' | '}
                {message.metadata.averageTokenTime.toFixed(2)} tokens/s
              </span>
            </div>
          )}
        </div>
        <IconButton
          onClick={() => copyToClipboard(message.content as string)}
          variant="ghost"
          className="h-8 w-8 opacity-0 group-hover:opacity-100 max-sm:hidden"
        >
          {isCopied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
        </IconButton>
      </div>
    </div>
  );
}
